/* STAS admin panel for the portal's welcome music.
 * Renders inside /admin/portal-campaigns above the campaigns list.
 *
 * Features:
 *  - Master enable/disable toggle
 *  - 30-track library with preview-on-tap (single audio element shared)
 *  - Volume slider (0–100%)
 *  - Save on change (debounced via explicit save button + per-toggle persistence)
 */
import { useEffect, useRef, useState } from "react";
import { Music2, Play, Pause, Volume2, Check, ToggleLeft, ToggleRight } from "lucide-react";

import { api } from "@/api/client";

export default function PortalMusicPanel({ theme, dir, css }) {
  const [library, setLibrary] = useState([]);
  const [settings, setSettings] = useState(null);   // { enabled, active_track_id, volume }
  const [previewId, setPreviewId] = useState(null);  // currently-previewing track id
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/portal-music");
      setLibrary(data.library || []);
      setSettings(data.settings);
    } catch (e) { setMsg(e?.response?.data?.detail || "تعذّر التحميل"); }
  };
  useEffect(() => { load(); }, []);

  // Stop any preview audio when this component unmounts.
  useEffect(() => () => { audioRef.current?.pause(); audioRef.current = null; }, []);

  const togglePreview = (track) => {
    if (previewId === track.id) {
      audioRef.current?.pause();
      setPreviewId(null);
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(track.url);
    a.volume = settings?.volume ?? 0.4;
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    a.play().then(() => setPreviewId(track.id)).catch(() => {
      setMsg("تعذّر تشغيل المعاينة — تأكد من الاتصال بالإنترنت");
    });
    a.addEventListener("ended", () => setPreviewId(null));
  };

  const update = async (patch) => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.patch("/admin/portal-music", patch);
      setSettings(data);
      // Apply new volume to current preview immediately.
      if (audioRef.current && patch.volume !== undefined) audioRef.current.volume = data.volume;
    } catch (e) { setMsg(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  if (!settings) return null;

  const enabled = !!settings.enabled;
  const activeId = settings.active_track_id;

  return (
    <div data-testid="portal-music-panel" style={{
      marginBottom: 20, padding: 16, borderRadius: 16,
      background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <Music2 size={20} color={theme.gold} strokeWidth={2.4} />
        <h2 style={{ fontSize: 16, fontWeight: 900, color: theme.text, margin: 0 }}>موسيقى الترحيب</h2>
        <button
          onClick={() => update({ enabled: !enabled })}
          disabled={busy}
          data-testid="portal-music-enable-toggle"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            marginInlineStart: "auto",
            padding: "5px 12px", borderRadius: 999, border: "none", cursor: "pointer",
            background: enabled ? "rgba(22,163,74,0.15)" : theme.surfaceAlt,
            color: enabled ? "#16a34a" : theme.textMuted,
            fontSize: 12, fontWeight: 900,
          }}
        >
          {enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          {enabled ? "مفعّلة" : "متوقفة"}
        </button>
      </div>

      <p style={{ fontSize: 12, color: theme.textMuted, marginTop: 0, marginBottom: 12, lineHeight: 1.7 }}>
        تظهر فقط في الواجهة الترحيبية قبل دخول العميل لأي زر — يقدر العميل يوقفها من زر صغير في الزاوية.
        لو الموسيقى متوقفة من هنا، لن يراها العميل إطلاقاً.
      </p>

      {/* Volume */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <Volume2 size={16} color={theme.gold} />
        <input
          type="range" min={0} max={1} step={0.05}
          value={settings.volume ?? 0.4}
          onChange={(e) => update({ volume: parseFloat(e.target.value) })}
          data-testid="portal-music-volume"
          style={{ flex: 1, accentColor: theme.gold }}
        />
        <span style={{ fontSize: 11, color: theme.textMuted, minWidth: 32, textAlign: "center" }}>
          {Math.round((settings.volume ?? 0.4) * 100)}٪
        </span>
      </div>

      {/* Library grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 8, maxHeight: 360, overflowY: "auto", padding: 4,
      }}>
        {library.map((t) => {
          const isActive = t.id === activeId;
          const isPlaying = previewId === t.id;
          return (
            <div key={t.id} data-testid={`music-track-${t.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: 10,
                borderRadius: 10,
                background: isActive ? "rgba(212,175,55,0.10)" : theme.surfaceAlt,
                border: `1.5px solid ${isActive ? theme.gold : "transparent"}`,
                transition: "all 150ms ease",
              }}
            >
              <button
                onClick={() => togglePreview(t)}
                title={isPlaying ? "إيقاف المعاينة" : "معاينة"}
                data-testid={`music-preview-${t.id}`}
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: isPlaying ? theme.gold : "transparent",
                  color: isPlaying ? "#0a0a0a" : theme.text,
                  border: `1px solid ${theme.borderAccent}`, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <div style={{ flex: 1, minWidth: 0, textAlign: dir === "rtl" ? "right" : "left" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: theme.text }}>{t.title_ar}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 1 }}>
                  {t.mood} · {Math.round(t.duration / 60)} د {t.duration % 60} ث
                </div>
              </div>
              <button
                onClick={() => update({ active_track_id: t.id })}
                disabled={isActive || busy}
                data-testid={`music-select-${t.id}`}
                title={isActive ? "المسار النشط" : "اختيار"}
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  background: isActive ? theme.gold : "transparent",
                  color: isActive ? "#0a0a0a" : theme.textMuted,
                  border: `1px solid ${isActive ? theme.gold : theme.border}`,
                  cursor: isActive ? "default" : "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Check size={14} strokeWidth={2.6} />
              </button>
            </div>
          );
        })}
      </div>

      {msg && <div style={{ ...css.err, marginTop: 10 }} data-testid="portal-music-error">{msg}</div>}
    </div>
  );
}
